import axios from 'axios'

export const TOKEN_KEY = 'bf_token'
export const USER_KEY = 'bf_user'

const ACTIVITY_KEY = 'bf_last_activity'
const SESSION_EVENT_KEY = 'bf_session_event'
const IDLE_MS = 30 * 60 * 1000
const HEARTBEAT_MS = 5 * 60 * 1000
const ACTIVITY_THROTTLE_MS = 1000
const REAL_ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel']

let started = false
let routerRef = null
let lastRecordedAt = 0
let lastHeartbeatAt = 0
let checkTimer = null
let heartbeatPending = false

function decodePayload(token) {
  if (typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length !== 3 || parts.some(part => !part)) return null
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))
      .split('').map(char => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''))
    return JSON.parse(json)
  } catch (e) {
    return null
  }
}

export function isValidBlogToken(token = localStorage.getItem(TOKEN_KEY)) {
  const payload = decodePayload(token)
  return !!(
    payload &&
    typeof payload.exp === 'number' &&
    payload.exp * 1000 > Date.now() &&
    payload.type === 'blog' &&
    ['member', 'guest'].includes(payload.userType) &&
    payload.blogUserId != null
  )
}

export function clearSession(reason = 'invalid') {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
  localStorage.removeItem(ACTIVITY_KEY)
  localStorage.setItem(SESSION_EVENT_KEY, `${Date.now()}:${reason}`)
  window.dispatchEvent(new CustomEvent('bf-session-invalidated', { detail: reason }))
}

export function markAuthenticated() {
  const now = Date.now()
  localStorage.setItem(ACTIVITY_KEY, String(now))
  localStorage.setItem(SESSION_EVENT_KEY, `${now}:login`)
  lastRecordedAt = now
  lastHeartbeatAt = now
}

function redirectHome() {
  if (routerRef && routerRef.currentRoute.path !== '/') {
    routerRef.replace('/').catch(() => {})
  }
}

function invalidate(reason) {
  clearSession(reason)
  redirectHome()
}

function recordActivity() {
  if (!localStorage.getItem(TOKEN_KEY)) return
  const now = Date.now()
  if (now - lastRecordedAt < ACTIVITY_THROTTLE_MS) return
  lastRecordedAt = now
  localStorage.setItem(ACTIVITY_KEY, String(now))
}

function lastActivityAt() {
  const value = Number(localStorage.getItem(ACTIVITY_KEY))
  return Number.isFinite(value) ? value : 0
}

async function heartbeat() {
  const token = localStorage.getItem(TOKEN_KEY)
  const now = Date.now()
  if (
    heartbeatPending ||
    !isValidBlogToken(token) ||
    now - lastActivityAt() > HEARTBEAT_MS ||
    now - lastHeartbeatAt < HEARTBEAT_MS
  ) return

  heartbeatPending = true
  lastHeartbeatAt = now
  try {
    const response = await axios.post('/api/blog/auth/heartbeat', {}, {
      timeout: 15000,
      headers: { Authorization: `Bearer ${token}` }
    })
    if (response.data && response.data.code === 200) {
      const data = response.data.data
      const refreshedToken = typeof data === 'string' ? data : data && data.token
      if (refreshedToken) localStorage.setItem(TOKEN_KEY, refreshedToken)
    } else if (response.data && (response.data.code === 401 || response.data.code === 403)) {
      invalidate('unauthorized')
    }
  } catch (error) {
    const status = error.response && error.response.status
    if (status === 401 || status === 403) invalidate('unauthorized')
  } finally {
    heartbeatPending = false
  }
}

function checkSession() {
  const token = localStorage.getItem(TOKEN_KEY)
  if (!token) return
  if (!isValidBlogToken(token)) {
    invalidate('expired')
    return
  }
  if (Date.now() - lastActivityAt() >= IDLE_MS) {
    invalidate('idle')
    return
  }
  heartbeat()
}

function handleStorage(event) {
  if (event.key === TOKEN_KEY && !event.newValue) redirectHome()
  if (event.key === SESSION_EVENT_KEY && event.newValue && !event.newValue.endsWith(':login')) {
    redirectHome()
  }
}

export function startSessionManager(router) {
  if (started) return
  started = true
  routerRef = router
  lastHeartbeatAt = Date.now()
  REAL_ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, recordActivity, { passive: true }))
  window.addEventListener('storage', handleStorage)
  window.addEventListener('bf-session-invalidated', redirectHome)
  checkTimer = window.setInterval(checkSession, 15000)
  checkSession()
}

export async function logoutSession() {
  const token = localStorage.getItem(TOKEN_KEY)
  try {
    if (token) {
      await axios.post('/api/blog/auth/logout', {}, {
        timeout: 15000,
        headers: { Authorization: `Bearer ${token}` }
      })
    }
  } catch (e) {
    // 本地退出不应被网络故障阻塞。
  } finally {
    invalidate('logout')
  }
}

export function stopSessionManager() {
  if (!started) return
  REAL_ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, recordActivity))
  window.removeEventListener('storage', handleStorage)
  window.removeEventListener('bf-session-invalidated', redirectHome)
  window.clearInterval(checkTimer)
  checkTimer = null
  started = false
}
